import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";

/**
 * One shared feed of in-app notifications for the bell and the BuiltBuddy bubbles — a single 60 s poll no matter how
 * many components listen. Rows come from GET /api/notifications (unread, newest first). Fires the window event
 * `builtos:notifications` when new rows land so open panels (BuiltBuddy thread) can re-sync.
 */
const POLL_MS = 60000;
export const RFI_KIND = "rfi_answered";

let state = { total: 0, items: [], loaded: false };
let listeners = new Set();
let timer = null;
let lastTotal = null;
let inflight = null;

const emit = () => listeners.forEach((fn) => fn());
const set = (next) => {
  state = next;
  emit();
};

export const refreshNotifications = () => {
  if (inflight) return inflight;
  inflight = api
    .get("/notifications")
    .then((r) => {
      const d = r.data || {};
      const next = { total: d.total || 0, items: d.items || [], loaded: true };
      if (lastTotal !== null && next.total > lastTotal) window.dispatchEvent(new CustomEvent("builtos:notifications", { detail: next }));
      lastTotal = next.total;
      set(next);
      return next;
    })
    .catch(() => state)
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

const start = () => {
  if (timer) return;
  refreshNotifications();
  timer = setInterval(refreshNotifications, POLL_MS);
  window.addEventListener("focus", onWake);
  document.addEventListener("visibilitychange", onWake);
};
const stop = () => {
  if (timer) clearInterval(timer);
  timer = null;
  window.removeEventListener("focus", onWake);
  document.removeEventListener("visibilitychange", onWake);
};

// coming back to the tab (or the app window) re-checks straight away — throttled so focus flicker doesn't spam the API
let lastWake = 0;
function onWake() {
  if (document.visibilityState === "hidden") return;
  const now = Date.now();
  if (now - lastWake < 10000) return;
  lastWake = now;
  refreshNotifications();
}

const subscribe = (fn) => {
  listeners.add(fn);
  start();
  return () => {
    listeners.delete(fn);
    if (!listeners.size) stop();
  };
};

/** Optimistic local removal (the server call follows). */
const drop = (pred) => {
  const items = state.items.filter((n) => !pred(n));
  const removed = state.items.length - items.length;
  lastTotal = Math.max(0, state.total - removed);
  set({ ...state, items, total: lastTotal });
};

export const markNotificationRead = (id) => {
  drop((n) => n.id === id);
  return api.post(`/notifications/${id}/read`).catch(() => {});
};

export const markAllNotificationsRead = () => {
  drop(() => true);
  return api.post("/notifications/read-all").catch(() => {});
};

/** Clears one job's rows of a kind — the BuiltBuddy bubble does this when the panel opens on that job. */
export const markJobNotificationsRead = (jobId, kind = RFI_KIND) => {
  if (!jobId) return Promise.resolve();
  const before = state.items.some((n) => n.job_id === jobId && (!kind || n.kind === kind));
  drop((n) => n.job_id === jobId && (!kind || n.kind === kind));
  if (!before) return Promise.resolve();
  return api.post(`/notifications/read-all?job_id=${encodeURIComponent(jobId)}${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`).catch(() => {});
};

/** Reset between logins so one user's rows never linger for the next. */
export const resetNotifications = () => {
  lastTotal = null;
  set({ total: 0, items: [], loaded: false });
};

export const useNotifications = () => useSyncExternalStore(subscribe, () => state, () => state);

/** Unread RFI-reply rows (optionally for one job) — drives the bubble badge + pulse. */
export const rfiUnread = (items, jobId = null) => (items || []).filter((n) => n.kind === RFI_KIND && (!jobId || n.job_id === jobId));
